import Container from '@/components/atoms/Container'
import Icon from '@/components/atoms/Icon'
import { useAppSelector } from '@/store/hooks'
import { Link, useMatch, useNavigate } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { Menu, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import classNames from 'classnames'
import useIsCurrentLink from '@/utils/hooks/useIsCurrentLink'
import LinkManager from '@/utils/helpers/LinkManager'

export function Topbar() {
	const {
		auth: { user, company },
	} = useAppSelector((state) => state)

	const isContentCourse = useMatch('/cursos/:slug/:slug')
	const navigate = useNavigate()

	const baseUrl = '/'
	const linkManager = new LinkManager(baseUrl)

	function createLink(to: string) {
		return linkManager.create(to)
	}

	function useIsCurrentLink_(to: string) {
		return !!useIsCurrentLink(createLink(to))
	}

	const isCursosActive = useIsCurrentLink_('/')
	const isMentoriasActive = useIsCurrentLink_('/mentorias')
	const isHabilidadesActive = useIsCurrentLink_('/mapeamento-habilidades')

	const navItens = [
		{
			label: 'Cursos',
			icon: <Icon name='book-3' width='24' height='24' />,
			link: createLink('/'),
			current: isCursosActive,
		},
		...(user?.white_label
			? [
					{
						label: 'Map. Habilidades',
						icon: <Icon name='map' width='24' height='24' />,
						link: createLink('/mapeamento-habilidades'),
						current: isHabilidadesActive,
					},
				]
			: []),
		...(!user?.white_label
			? [
					{
						label: 'Mentorias',
						icon: <Icon name='user-group' width='24' height='24' />,
						link: createLink('/mentorias'),
						current: isMentoriasActive,
					},
				]
			: []),
	]

	const optionsDropdown = [
		{
			onClick: () => navigate('/cursos'),
			label: 'Cursos',
			icon: <Icon name='book-3' width='24' height='24' />,
			roles: !user?.white_label && !user?.modo_curso_unico,
			disabled: false,
			className: '',
		},
		{
			onClick: () => navigate('/mentorias'),
			label: 'Mentorias',
			icon: <Icon name='user-group' width='24' height='24' />,
			roles: !user?.white_label && !user?.modo_curso_unico,
			disabled: false,
			className: '',
		},
		{
			onClick: () => navigate('/mapeamento-habilidades'),
			label: 'Map. Habilidades',
			icon: <Icon name='map' width='24' height='24' />,
			roles: user?.white_label,
			disabled: false,
			className: '',
		},
		{
			onClick: () => window.open('https://help.fluency.io', '_blank'),
			label: 'Ajuda',
			icon: <Icon name='help' width='24' height='24' />,
			roles: true,
			disabled: false,
			className: '',
		},
		{
			onClick: () =>
				window.open(
					'https://fluency-b2b-prod.s3.amazonaws.com/email/files/Fluency+Corporate+-+Guia+do+Aluno.pdf',
					'_blank'
				),
			label: 'Baixar Manual do Aluno',
			icon: <Icon name='download' width='24' height='24' />,
			roles: !user?.white_label,
			disabled: false,
			className: '',
		},
		{
			onClick: () => navigate('/sair'),
			label: 'Sair',
			icon: <Icon name='logout' width='24' height='24' />,
			roles: true,
			disabled: false,
			className: '',
		},
	]

	const [scrolling, setScrolling] = useState(isContentCourse ? true : false)

	useEffect(() => {
		if (!isContentCourse) {
			const handleScroll = () => {
				if (window.scrollY > 0) {
					setScrolling(true)
				} else {
					setScrolling(false)
				}
			}

			window.addEventListener('scroll', handleScroll)

			return () => {
				window.removeEventListener('scroll', handleScroll)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<header
				className={twMerge(
					'w-full sticky top-0 z-30 h-[90px] md:py-2 bg-zinc-950 border-b border-zinc-700',
					classNames({
						'shadow-sm': scrolling,
					})
				)}
			>
				<Container className='w-full !max-w-[1420px] xl:px-8 2xl:px-0 h-[81px] flex items-center justify-between'>
					<div className='flex items-center gap-16'>
						<div className='flex items-center'>
							<Link to='/'>
								{(() => {
									if (!company) return <></>

									if (company?.white_label && company?.logo) {
										return (
											<div className='relative flex justify-center items-center w-[125px] md:w-[125px] h-auto my-4 md:my-5'>
												<img
													src={company.logo}
													alt='Logo company'
													className='w-full h-full'
												/>
											</div>
										)
									}

									return (
										<Icon
											name='logo'
											width='125'
											height='45'
											fill='currentColor'
											className='w-[125px] h-[45px] md:w-[125px] my-4 md:my-5 text-[#150e1d] dark:text-white'
										/>
									)
								})()}
							</Link>
						</div>
						{!user?.modo_curso_unico && (
							<div className='hidden lg:flex gap-3'>
								{navItens?.map((item, key) => {
									return (
										<Link
											key={key}
											to={item.link}
											className={twMerge(
												'flex items-center min-w-max gap-2 py-2 px-3 font-semibold leading-none text-white opacity-80 hover:opacity-100 transition-opacity',
												classNames({
													'opacity-100 bg-brand-primary text-brand-text-primary rounded-lg':
														item.current,
												})
											)}
										>
											{item.icon}
											{item.label}
										</Link>
									)
								})}
							</div>
						)}
					</div>
					<div className='w-full flex justify-end items-center gap-4 2xl:gap-8'>
						<div className='flex gap-6'>
							<div
								className='flex rounded items-center'
								onClick={(e) => e.stopPropagation()}
							>
								<Menu as='div' className='relative inline-block text-left'>
									<div>
										<Menu.Button className='inline-flex items-center gap-2 md:gap-4 lg:gap-5'>
											<div className='flex'>
												<div className='flex font-medium text-sm leading-none justify-end items-center gap-3 z-20'>
													{(() => {
														return (
															<div className='relative flex justify-center items-center w-12 h-12 border-2 border-[#150e1d] overflow-clip bg-gray-100 rounded-full dark:bg-brand-primary'>
																<Icon
																	name='default-avatar'
																	width='33'
																	height='33'
																/>
															</div>
														)
													})()}
												</div>
												<div className='flex font-medium text-sm leading-none justify-end items-center gap-3 -ml-2 z-10'>
													{(() => {
														if (company?.avatar) {
															return (
																<div className='relative flex justify-center items-center w-11 h-11 overflow-clip bg-gray-100 rounded-full dark:bg-brand-primary'>
																	<img
																		src={company.avatar}
																		alt='Avatar User'
																		className='w-full h-full'
																	/>
																</div>
															)
														}

														return (
															<div className='relative flex justify-center items-center w-11 h-11 overflow-clip bg-gray-100 rounded-full dark:bg-brand-primary'>
																<Icon
																	name='default-avatar'
																	width='33'
																	height='33'
																/>
															</div>
														)
													})()}
												</div>
											</div>
											<div className='hidden lg:flex gap-3'>
												<span className='flex text-[#150e1d]/70 dark:text-white font-semibold text-base'>
													{user?.name}
												</span>
												<Icon name='arrow-down' width='24' height='24' />
											</div>
										</Menu.Button>
									</div>

									<Transition
										as={Fragment}
										enter='transition ease-out duration-200'
										enterFrom='transform opacity-0 -translate-y-1 scale-75'
										enterTo='transform opacity-100 translate-y-0 scale-100'
										leave='transition ease-in duration-75'
										leaveFrom='transform opacity-100 scale-100'
										leaveTo='transform opacity-0 scale-95'
									>
										<Menu.Items className='absolute right-0 z-10 mt-3 py-1 px-4 min-w-[289px] origin-bottom-right rounded-lg shadow-lg focus:outline-none bg-zinc-900 border border-gray-300/30 dark:border-[#2c2634]'>
											<div className='flex flex-col'>
												{optionsDropdown?.map((option) => {
													if (!option?.roles)
														return <div key={option.label}></div>

													return (
														<Menu.Item
															key={option.label}
															disabled={option.disabled}
														>
															{({ active, close }) => (
																<button
																	className={twMerge(
																		classNames(
																			active
																				? 'text-brand-primary dark:text-zinc-50 dark:text-opacity-100 rounded-lg'
																				: 'text-zinc-50 dark:text-opacity-80',
																			'w-full items-center flex py-4 px-2 font-medium justify-between last-of-type:border-t border-[#ffffff]/5',
																			{ 'cursor-default': option?.disabled }
																		),
																		option.className
																	)}
																	onClick={(e) => {
																		e.preventDefault()
																		close()
																		option?.onClick && option.onClick()
																	}}
																>
																	{option.label}
																	{option.icon}
																</button>
															)}
														</Menu.Item>
													)
												})}
											</div>
										</Menu.Items>
									</Transition>
								</Menu>
							</div>
						</div>
					</div>
				</Container>
			</header>
		</>
	)
}
